import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    MenuItem,
    Modal,
    Select,
    Typography
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import "./TimeSlotModal.css";
import { TimeSlot } from "../../data/timeSlots";
import { deleteTimeSlot } from "../../services/scheduleService";

interface DeleteTimeSlotModalProps {
    isOpen: boolean;
    onClose: () => void;
    timeSlots: TimeSlot[];
    eventId: string;
    initialSlot?: TimeSlot | null;
}

const DeleteTimeSlotModal: React.FC<DeleteTimeSlotModalProps> = ({
                                                                     isOpen,
                                                                     onClose,
                                                                     timeSlots,
                                                                     eventId,
                                                                     initialSlot = null
                                                                 }) => {
    const [selectedId, setSelectedId] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            // Pre-select if we're in the "X-click" flow
            setSelectedId(initialSlot?.TimeSlotId ?? "");
            setError(null);
        }
    }, [isOpen, initialSlot]);

    const handleDelete = async () => {
        const idToDelete = initialSlot?.TimeSlotId ?? selectedId;
        if (!idToDelete) return;

        setLoading(true);
        setError(null);

        try {
            await deleteTimeSlot({ TimeSlotId: idToDelete, EventId: eventId });
            onClose();
        } catch (e: any) {
            setError(e.message || "Failed to delete time slot.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={isOpen} onClose={onClose} aria-labelledby="delete-timeslot">
            <Box className="modalBox">
                <Typography variant="h6">
                    {initialSlot
                        ? `Are you sure you want to delete "${initialSlot.Title}" time slot?`
                        : "Delete Time Slot"}
                </Typography>

                {/* only show dropdown when not in the X-click flow */}
                {!initialSlot && (
                    <Select
                        className="formField"
                        fullWidth
                        value={selectedId}
                        onChange={(e) => setSelectedId(e.target.value as string)}
                        displayEmpty
                    >
                        <MenuItem value="" disabled>
                            -- Select a Time Slot --
                        </MenuItem>
                        {timeSlots.map((t) => (
                            <MenuItem key={t.TimeSlotId} value={t.TimeSlotId}>
                                {t.Title} ({new Date(t.StartsAt).toLocaleString()})
                            </MenuItem>
                        ))}
                    </Select>
                )}

                {error && <Typography className="errorText">{error}</Typography>}

                <Box className="actions">
                    <Button onClick={onClose} sx={{ mr: 1 }} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        variant="contained"
                        className="delete"
                        disabled={loading || (!initialSlot && !selectedId)}
                        startIcon={loading ? <CircularProgress size={20} /> : undefined}
                    >
                        {loading
                            ? "Deleting..."
                            : initialSlot
                                ? "Yes, Delete"
                                : "Delete"}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default DeleteTimeSlotModal;